import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import { Box, Button, Chip, IconButton, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, useGridApiRef } from '@mui/x-data-grid';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useEffect } from 'react';
import cardIdImg from 'v2/assets/card-id.png';
import { AccessCardStatusColumnHeader } from 'v2/components/AccessCardStatusColumnHeader/AccessCardStatusColumnHeader';
import CardChip, { CardChipColor } from 'v2/components/CardChip/CardChip';
import { ColumnHeaderWithTooltip } from 'v2/components/ColumnHeaderWithTooltip/ColumnHeaderWithTooltip';
import { getOverviewDataGridProps } from 'v2/components/DataGrid/getOverviewDataGridProps';
import { renderHeaderWithId } from 'v2/components/DataGrid/renderHeaderWithId';
import { DataGridCard, DataGridCards } from 'v2/components/DataGridCards/DataGridCards';
import useModalContext from 'v2/components/Modal/useModalContext';
import RepeatOverflowCounter from 'v2/components/RepeatOverflowCounter/RepeatOverflowCounter';
import { TextCell, TwoLineCell } from 'v2/components/TextCell/TextCell';
import { DATAGRID_ROW_HEIGHT, EMPTY_VALUE_PLACEHOLDER } from 'v2/constants';
import { STATUS_SORT_PRIORITY } from 'v2/filters/accessCards/cardFilterConstants';
import { AccessCardEntry, CardStatus } from 'v2/filters/accessCards/cardFilterTypes';
import { formatDate } from 'v2/utils/dateTime';
import BlockAccessCardModal from './BlockAccessCardModal';
import BlockedAccessCardModal from './BlockedAccessCardModal';

interface Props {
  entries: AccessCardEntry[];
  loading?: boolean;
  onBlockCard: (uuid: string, reason: string) => Promise<void>;
}

const BLOCK_BUTTON_CLASS = 'block-card-btn';

const canBlockCard = (entry: AccessCardEntry): boolean => entry.assigned && !entry.blocked;

const AccessCardsTable: React.FC<Props> = ({ entries, loading = false, onBlockCard }) => {
  const { translation } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const { openModal } = useModalContext();
  const apiRef = useGridApiRef();

  useEffect(() => {
    if (!loading && apiRef.current) {
      apiRef.current.autosizeColumns({ columns: ['actions'], includeHeaders: false });
    }
  }, [loading, apiRef]);

  const chipMap: Record<CardStatus, { label: string; color: CardChipColor }> = {
    cardBlocked: { label: translation.blocked, color: 'secondary' },
    cardExpired: { label: translation.card_expired, color: 'error' },
    cardExpiresSoon: { label: translation.card_expires_soon, color: 'warning' },
    userDeleted: { label: translation.user_deleted, color: 'error' },
    accessRightsChanged: { label: translation.rights_changed, color: 'error' },
    userExpiresSoon: { label: translation.user_expires_soon, color: 'warning' }
  };

  const handleOpenBlockedModal = (entry: AccessCardEntry) => {
    if (!entry.blockedSince) return;
    openModal(
      <BlockedAccessCardModal
        cardUuid={entry.uuid}
        blockedSince={entry.blockedSince}
        blockedReason={entry.blockedReason}
      />
    );
  };

  const handleOpenBlockModal = (entry: AccessCardEntry) => {
    openModal(<BlockAccessCardModal cardUuid={entry.uuid} onConfirm={onBlockCard} />);
  };

  const sortStatusesByPriority = (statuses: CardStatus[]): CardStatus[] =>
    [...statuses].sort((a, b) => STATUS_SORT_PRIORITY[b] - STATUS_SORT_PRIORITY[a]);

  const renderStatusChip = (entry: AccessCardEntry) => {
    if (entry.statuses.length === 0) return <CardChip label={translation.active} color="success" />;
    const sortedStatuses = sortStatusesByPriority(entry.statuses);
    return (
      <RepeatOverflowCounter
        variant="chip"
        renderMore={(count) => {
          const hiddenLabels = sortedStatuses
            .slice(-count)
            .map((status) => chipMap[status].label)
            .join(', ');
          return (
            <Tooltip title={hiddenLabels} placement="top">
              <Chip size="small" variant="filled" label={`+${count}`} />
            </Tooltip>
          );
        }}
      >
        {sortedStatuses.map((status) => {
          const { label, color } = chipMap[status];
          return <CardChip key={status} label={label} color={color} />;
        })}
      </RepeatOverflowCounter>
    );
  };

  const renderBlockedReasonIcon = (entry: AccessCardEntry) => (
    <IconButton
      id={`access-card-${entry.id}-blocked-reason-button`}
      size="small"
      aria-label={translation.card_blocked}
      onClick={(event) => {
        event.stopPropagation();
        handleOpenBlockedModal(entry);
      }}
    >
      <DescriptionOutlined fontSize="small" />
    </IconButton>
  );

  if (isMobile) {
    return (
      <DataGridCards loading={loading}>
        {entries.map((entry) => (
          <DataGridCard key={entry.id} id={`row-${entry.id}`}>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="flex-start">
                <Stack overflow="hidden" minWidth={0} flexGrow={1}>
                  <Typography variant="caption" color="textSecondary" display="block">
                    {translation.card_id}
                  </Typography>
                  <Typography variant="body2" noWrap>
                    {entry.uuid}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
                  {renderStatusChip(entry)}
                  {entry.statuses.includes('cardBlocked') && renderBlockedReasonIcon(entry)}
                </Stack>
              </Stack>
              {entry.visitorName && (
                <Stack overflow="hidden" minWidth={0}>
                  <Typography variant="caption" color="textSecondary" display="block">
                    {translation.assigned_to}
                  </Typography>
                  <Typography variant="body2" noWrap>
                    {entry.visitorName}
                  </Typography>
                  {entry.visitorEmail && (
                    <Typography variant="caption" color="textSecondary" display="block" noWrap>
                      {entry.visitorEmail}
                    </Typography>
                  )}
                </Stack>
              )}
              {entry.expiryDate && (
                <Stack>
                  <Typography variant="caption" color="textSecondary" display="block">
                    {translation.card_valid_until}
                  </Typography>
                  <Stack direction="row" gap={1} alignItems="baseline">
                    <Typography variant="body2">{formatDate(entry.expiryDate, 'dateOnlyLong')}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {formatDate(entry.expiryDate, 'timeOnly')}
                    </Typography>
                  </Stack>
                </Stack>
              )}
              {canBlockCard(entry) && (
                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    id={`access-card-${entry.id}-block-button`}
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => handleOpenBlockModal(entry)}
                  >
                    {translation.block}
                  </Button>
                </Stack>
              )}
            </Stack>
          </DataGridCard>
        ))}
      </DataGridCards>
    );
  }

  const headerWithId = renderHeaderWithId();

  const columns: GridColDef<AccessCardEntry>[] = [
    {
      field: 'uuid',
      headerName: translation.card_id,
      flex: 1,
      minWidth: 150,
      cellClassName: 'mono-cell',
      renderHeader: renderHeaderWithId(undefined, () => (
        <ColumnHeaderWithTooltip
          label={translation.card_id}
          tooltip={
            <Stack spacing={1} alignItems="center">
              <Typography variant="caption">{translation.card_id_column_tooltip}</Typography>
              <Box component="img" src={cardIdImg} alt={translation.card_id} sx={{ width: 220, borderRadius: 1 }} />
            </Stack>
          }
        />
      )),
      renderCell: ({ value }: GridRenderCellParams<AccessCardEntry, string>) => (
        <TextCell variant="title">{value}</TextCell>
      )
    },
    {
      field: 'visitorName',
      headerName: translation.assigned_to,
      flex: 3,
      renderHeader: renderHeaderWithId(undefined, () => (
        <ColumnHeaderWithTooltip
          label={translation.assigned_to}
          tooltip={<Typography variant="caption">{translation.assigned_to_column_tooltip}</Typography>}
        />
      )),
      renderCell: ({ row }: GridRenderCellParams<AccessCardEntry>) => {
        if (!row.visitorName) return EMPTY_VALUE_PLACEHOLDER;
        return <TwoLineCell title={row.visitorName} content={row.visitorEmail} />;
      }
    },
    {
      field: 'expiryDate',
      headerName: translation.card_valid_until,
      flex: 1,
      minWidth: 175,
      type: 'dateTime',
      valueGetter: (value: string | null) => (value ? new Date(value) : null),
      renderHeader: renderHeaderWithId(undefined, () => (
        <ColumnHeaderWithTooltip
          label={translation.card_valid_until}
          tooltip={
            <Typography variant="caption" sx={{ whiteSpace: 'pre-line' }}>
              {translation.card_valid_until_column_tooltip}
            </Typography>
          }
        />
      )),
      renderCell: ({ value }: GridRenderCellParams<AccessCardEntry, Date | null>) => {
        if (!value) return EMPTY_VALUE_PLACEHOLDER;
        return <TwoLineCell title={formatDate(value, 'dateOnlyLong')} content={formatDate(value, 'timeOnly')} />;
      }
    },
    {
      field: 'status',
      headerName: translation.status,
      flex: 2,
      minWidth: 115,
      valueGetter: (_value: unknown, row: AccessCardEntry) =>
        row.statuses.length === 0 ? null : Math.max(...row.statuses.map((status) => STATUS_SORT_PRIORITY[status])),
      renderHeader: renderHeaderWithId(undefined, () => <AccessCardStatusColumnHeader />),
      renderCell: ({ row }: GridRenderCellParams<AccessCardEntry>) => renderStatusChip(row)
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      resizable: false,
      align: 'right',
      headerAlign: 'right',
      renderHeader: headerWithId,
      renderCell: ({ row }: GridRenderCellParams<AccessCardEntry>) => {
        if (row.blocked) {
          return renderBlockedReasonIcon(row);
        }
        if (!canBlockCard(row)) return null;
        return (
          <Box pr={1}>
            <Button
              id={`access-card-${row.id}-block-button`}
              className={BLOCK_BUTTON_CLASS}
              variant="text"
              color="error"
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                handleOpenBlockModal(row);
              }}
            >
              {translation.block}
            </Button>
          </Box>
        );
      }
    }
  ];

  const columnVisibilityModel = {
    uuid: true,
    visitorName: true,
    expiryDate: isLgUp,
    status: true,
    actions: true
  };

  const overviewDataGridProps = getOverviewDataGridProps({ rowCount: entries.length, loading });

  return (
    <DataGrid
      {...overviewDataGridProps}
      apiRef={apiRef}
      className="not-clickable"
      rows={entries}
      columns={columns}
      columnVisibilityModel={columnVisibilityModel}
      rowHeight={DATAGRID_ROW_HEIGHT}
      initialState={{
        ...overviewDataGridProps.initialState,
        sorting: { sortModel: [{ field: 'uuid', sort: 'asc' }] }
      }}
      sx={{
        [`& .${BLOCK_BUTTON_CLASS}`]: {
          visibility: 'hidden',
          opacity: 0,
          transition: 'opacity 0.2s ease-in-out'
        },
        [`& .MuiDataGrid-row:hover .${BLOCK_BUTTON_CLASS}`]: {
          visibility: 'visible',
          opacity: 1
        },
        '@media (hover: none), (pointer: coarse)': {
          [`& .${BLOCK_BUTTON_CLASS}`]: { visibility: 'visible', opacity: 1 }
        }
      }}
    />
  );
};

export default AccessCardsTable;
